import React, { useState } from 'react';
import { X, UserPlus, Mail } from 'lucide-react';

const DEPARTMENTS = ['Marketing', 'Driver', 'Finance', 'Sales', 'Operations', 'Technology', 'Legal'];
const isEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

/**
 * Editable list of assignee contacts (name + email + department).
 * Everyone in this list is emailed on assignment and on every status change.
 */
export const AssigneeContacts = ({ contacts, onChange, suggestions = [], compact = false }) => {
  const [draft, setDraft] = useState({ name: '', email: '', department: '' });
  const [error, setError] = useState('');

  const add = (c) => {
    const entry = { name: (c.name || '').trim(), email: (c.email || '').trim(), department: c.department || null };
    if (!entry.name) return setError('Name is required');
    if (!isEmail(entry.email)) return setError('Enter a valid email address');
    if (contacts.some((x) => x.email.toLowerCase() === entry.email.toLowerCase())) return setError('That email is already added');
    setError('');
    onChange([...contacts, entry]);
    setDraft({ name: '', email: '', department: '' });
  };

  const remove = (email) => onChange(contacts.filter((c) => c.email !== email));

  const unusedSuggestions = suggestions.filter(
    (s) => !contacts.some((c) => c.email.toLowerCase() === (s.email || '').toLowerCase())
  );

  return (
    <div data-testid="assignee-contacts-editor">
      {contacts.length > 0 && (
        <ul className="space-y-2 mb-3">
          {contacts.map((c) => (
            <li
              key={c.email}
              className="flex items-center gap-3 bg-cyan-500/10 border border-cyan-400/25 rounded-xl px-3 py-2.5"
              data-testid={`assignee-chip-${c.email}`}
            >
              <div className="w-8 h-8 rounded-full bg-cyan-500/20 text-cyan-200 flex items-center justify-center text-xs font-bold shrink-0">
                {(c.name || '?').slice(0, 2).toUpperCase()}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-sm text-white font-medium truncate">{c.name}</div>
                <div className="text-xs text-slate-400 truncate flex items-center gap-1">
                  <Mail className="w-3 h-3 shrink-0" /> {c.email}
                  {c.department && <span className="text-cyan-300/70">· {c.department}</span>}
                </div>
              </div>
              <button
                type="button"
                onClick={() => remove(c.email)}
                className="w-9 h-9 flex items-center justify-center rounded-lg text-slate-400 hover:text-red-300 hover:bg-red-500/10 shrink-0"
                aria-label={`Remove ${c.name}`}
                data-testid={`assignee-remove-${c.email}`}
              >
                <X className="w-4 h-4" />
              </button>
            </li>
          ))}
        </ul>
      )}

      {unusedSuggestions.length > 0 && (
        <div className="mb-3">
          <p className="text-xs text-slate-500 mb-2">Suggested from past work orders</p>
          <div className="flex flex-wrap gap-2">
            {unusedSuggestions.map((s) => (
              <button
                key={s.email}
                type="button"
                onClick={() => add(s)}
                className="px-3 py-2 rounded-full text-xs border border-white/15 text-slate-300 hover:border-cyan-400/40 hover:text-cyan-200 min-h-[36px]"
                data-testid={`assignee-suggestion-${s.email}`}
              >
                + {s.name || s.email}
                {s.department && <span className="text-slate-500"> · {s.department}</span>}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className={`grid gap-2 ${compact ? 'grid-cols-1' : 'grid-cols-1 sm:grid-cols-[1fr_1fr_auto]'}`}>
        <input
          type="text"
          value={draft.name}
          onChange={(e) => setDraft({ ...draft, name: e.target.value })}
          placeholder="Full name"
          className="input-dark w-full min-h-[44px]"
          data-testid="assignee-name-input"
        />
        <input
          type="email"
          value={draft.email}
          onChange={(e) => setDraft({ ...draft, email: e.target.value })}
          placeholder="name@medexova.com"
          className="input-dark w-full min-h-[44px]"
          data-testid="assignee-email-input"
        />
        <div className="flex gap-2">
          <select
            value={draft.department}
            onChange={(e) => setDraft({ ...draft, department: e.target.value })}
            className="input-dark flex-1 min-h-[44px]"
            data-testid="assignee-dept-select"
          >
            <option value="">Dept (optional)</option>
            {DEPARTMENTS.map((d) => (
              <option key={d} value={d}>{d}</option>
            ))}
          </select>
          <button
            type="button"
            onClick={() => add(draft)}
            className="px-4 min-h-[44px] rounded-lg bg-cyan-500 text-black font-semibold hover:bg-cyan-400 flex items-center gap-1.5 shrink-0"
            data-testid="assignee-add-btn"
          >
            <UserPlus className="w-4 h-4" /> Add
          </button>
        </div>
      </div>

      {error && <p className="text-xs text-red-300 mt-2" data-testid="assignee-error">{error}</p>}
      <p className="text-xs text-slate-500 mt-2">
        Everyone listed gets an email when assigned and on every status change.
      </p>
    </div>
  );
};

export default AssigneeContacts;
